<template>
  <b-modal
    ref="modalRef"
    :title="$t('Verificação de segurança')"
    centered
    no-close-on-backdrop
    no-close-on-esc
    :ok-title="$t('Verificar')"
    :cancel-title="$t('Cancelar')"
    :ok-disabled="!isCodeValid || loading"
    dialog-class="reward-code-verification-modal"
    :hide-header-close="true"
    @ok.prevent="onVerify"
    @cancel="onCancel"
    @close="onCancel"
    @hidden="hideKeyboard"
    @shown="onModalShown"
  >
    <div class="verification-container">
      <div class="verification-message mb-4">
        <p class="text-center text-muted mb-0">
          {{ customerEmail ? `Digite o código de verificação enviado por notificação no celular cadastrado e para o e-mail ${customerEmail}. Se preferir, abra o app echope e acesse "Perfil > Segurança > Código de Verificação" para obter o código.` : '' }}
        </p>
      </div>

      <div class="code-input-wrapper">
        <b-form-group
          label="Código de Verificação"
          label-for="verification-code"
          class="mb-0"
        >
          <b-form-input
            id="verification-code"
            ref="codeInput"
            v-model="verificationCode"
            type="text"
            inputmode="numeric"
            maxlength="4"
            placeholder="____"
            autocomplete="off"
            class="text-center code-input"
            @input="onCodeInput"
            @keyup.enter="onVerify"
            @focus="onInputFocus"
          />
        </b-form-group>
      </div>

      <div class="resend-code-section">
        <button
          type="button"
          class="btn btn-link btn-sm text-muted"
          :disabled="resendCodeLoading"
          @click="onResendCode"
        >
          <span v-if="!resendCodeLoading">
            Reenviar código
          </span>
          <span v-else>
            <b-spinner small /> Reenviando...
          </span>
        </button>
      </div>

      <div
        v-if="errorMessage"
        class="alert alert-danger mt-3 mb-0"
        role="alert"
      >
        {{ errorMessage }}
      </div>

      <div
        v-if="successMessage"
        class="alert alert-success mt-3 mb-0"
        role="alert"
      >
        {{ successMessage }}
      </div>

      <div
        v-if="loading"
        class="text-center mt-3"
      >
        <b-spinner
          label="Verificando código..."
          type="border"
          variant="primary"
          small
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BFormGroup, BFormInput, BModal, BSpinner } from 'bootstrap-vue'
import { mapActions } from 'vuex'
import axiosInstance from '@/libs/axios'
import { loadingOverlay } from '@/mixins'

export default {
  components: {
    BModal,
    BFormGroup,
    BFormInput,
    BSpinner,
  },

  props: {
    showVirtualKeyboard: {
      type: Boolean,
      default: true,
    },
    showOverlayLoading: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [loadingOverlay],

  data() {
    return {
      verificationCode: '',
      loading: false,
      resendCodeLoading: false,
      errorMessage: '',
      successMessage: '',
      customerId: null,
      saleTotal: null,
      customerEmail: null,
    }
  },

  computed: {
    isCodeValid() {
      return this.verificationCode.length === 4 && /^\d{4}$/.test(this.verificationCode)
    },
  },

  methods: {
    ...mapActions('app/simpleKeyboard', ['showKeyboard', 'hideKeyboard']),

    async show(customerId, saleTotal) {
      try {
        this.customerId = customerId
        this.saleTotal = saleTotal
        this.verificationCode = ''
        this.errorMessage = ''
        this.successMessage = ''
        this.customerEmail = null
        this.loading = true

        if (this.showOverlayLoading) {
          this.showLoadingOverlay(this.$t('Gerando código de verificação...'))
        }

        // Chamar endpoint para gerar o código de verificação
        const response = await axiosInstance.post('/api/security/generate-verification-code', {
          customerId,
        })

        this.customerEmail = response.data.email

        // Mostrar o modal (o focus acontecerá em onModalShown)
        this.$refs.modalRef.show()
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Erro ao gerar código de verificação'
        this.$refs.modalRef.show()
      } finally {
        this.loading = false
        this.hideLoadingOverlay()
      }
    },

    onCodeInput(value) {
      // Aceitar apenas números
      this.verificationCode = value.replace(/\D/g, '').slice(0, 4)
      this.errorMessage = ''
      this.successMessage = ''
    },

    onInputFocus(event) {
      // Mostrar teclado virtual quando o usuário clica no input
      // Aguardar um pouco para garantir que o elemento está pronto
      if (this.showVirtualKeyboard) {
        setTimeout(() => {
          this.inputShowOsk(event)
        }, 50)
      }
    },

    async onResendCode() {
      try {
        this.resendCodeLoading = true
        this.errorMessage = ''
        this.successMessage = ''

        // Chamar endpoint para reenviar o código
        await axiosInstance.post('/api/security/generate-verification-code', {
          customerId: this.customerId,
        })

        this.successMessage = 'Código reenviado com sucesso. Verifique seu cadastro.'
        this.verificationCode = ''

        // Limpar mensagem de sucesso após 3 segundos
        setTimeout(() => {
          this.successMessage = ''
        }, 3000)

        this.$nextTick(() => {
          this.$refs.codeInput.focus()
        })
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Erro ao reenviar o código. Tente novamente.'
      } finally {
        this.resendCodeLoading = false
      }
    },

    async onVerify() {
      try {
        this.loading = true
        this.errorMessage = ''
        this.successMessage = ''

        // Chamar endpoint para validar o código
        const response = await this.$http.post('/api/security/validate-verification-code', {
          customerId: this.customerId,
          verificationCode: this.verificationCode,
          saleTotal: this.saleTotal,
        })

        if (response.status === 200) {
          this.$emit('verified', {
            customerId: this.customerId,
            saleTotal: this.saleTotal,
          })
          this.$refs.modalRef.hide()
        }
      } catch (error) {
        this.errorMessage = this.$t(error.response?.data) || 'Código inválido. Tente novamente.'
        this.verificationCode = ''
        this.$refs.codeInput.focus()
      } finally {
        this.loading = false
      }
    },

    inputShowOsk(event) {
      this.showKeyboard({
        el: event.target,
        layoutType: 'numeric',
        selectAllText: true,
      })
    },

    onModalShown() {
      // Quando o modal fica totalmente visível, fazer focus no input
      requestAnimationFrame(() => {
        this.focusAndShowKeyboard()
      })
    },

    focusAndShowKeyboard() {
      const {codeInput} = this.$refs
      if (!codeInput) {
        return
      }

      // Tentar encontrar o elemento input de várias formas
      let inputElement = null

      // Primeira tentativa: Se codeInput.$el é um input nativo
      if (codeInput.$el && codeInput.$el.tagName === 'INPUT') {
        inputElement = codeInput.$el
      }
      // Segunda tentativa: Procurar input dentro do $el
      else if (codeInput.$el) {
        inputElement = codeInput.$el.querySelector('input')
      }
      // Terceira tentativa: Via $el do componente pai (wrapper)
      else if (codeInput.querySelector) {
        inputElement = codeInput.querySelector('input')
      }

      // Se não encontrou, tentar usar focus() diretamente no componente
      if (!inputElement) {
        try {
          if (typeof codeInput.focus === 'function') {
            codeInput.focus()
          }
          if (typeof codeInput.$el?.focus === 'function') {
            codeInput.$el.focus()
          }
        } catch (e) {
          // Ignorar erros
        }
        return
      }

      // Se encontrou o elemento, fazer focus e mostrar teclado
      inputElement.focus()

      if (inputElement.value) {
        inputElement.select()
      }

      // Mostrar teclado virtual apenas se habilitado
      if (this.showVirtualKeyboard) {
        this.inputShowOsk({ target: inputElement })
      }
    },

    onCancel() {
      this.hideKeyboard()
      this.$emit('cancelled')
      this.$refs.modalRef.hide()
    },
  },
}
</script>

<style lang="scss" scoped>
.verification-container {
  padding: 1rem 0;
}

.verification-message {
  p {
    margin: 0;
  }
}

.code-input-wrapper {
  .code-input {
    font-size: 2rem;
    letter-spacing: 0.5rem;
    font-weight: bold;
    text-align: center;
    font-family: monospace;
    padding: 1rem;
    border: 2px solid #dee2e6;
    border-radius: 0.5rem;
    border: none;

    &:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    &::placeholder {
      letter-spacing: 0.5rem;
    }
  }
}

.resend-code-section {
  display: flex;
  justify-content: center;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;

  .btn-link {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    text-decoration: none;

    &:hover:not(:disabled) {
      text-decoration: underline;
      color: #0056b3 !important;
    }

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }
  }
}

:global(.reward-code-verification-modal) {
  width: 90vw !important;

  @media (min-width: 576px) {
    width: 500px !important;
  }
}
</style>

